import { useCallback, useEffect, useRef } from "react";

import { parser } from "components/organisms/manuscript/overleaf/languages/latex/visual/latex.mjs";
import { loadMathJax } from "../load-mathjax.js";
import { typesetNodeIntoElement } from "../utils/typeset-content.js";
import { useCodeMirrorViewContext } from "./codemirror-view-context.js";
import { CellInput } from "./cell-input.js";
import { useEditingContext } from "./contexts/editing-context.js";
import { TableSelection, useSelectionContext } from "./contexts/selection-context.js";
import { useTableContext } from "./contexts/table-context.js";

function classNames(...parts) {
    return parts.filter(Boolean).join(" ");
}

function renderableCellContent(content) {
    return content.replace(/\\footnotemark(?:\[(.*?)\])?/g, (_, marker) => {
        const normalized = marker?.trim() || "*";
        return `\\textsuperscript{${normalized}}`;
    });
}

export function Cell({ cellData, columnSpecification: columnSpecificationFromTabular, rowIndex, columnIndex, row }) {
    const columnSpecification = cellData.multiColumn
        ? cellData.multiColumn.columns.specification[0]
        : columnSpecificationFromTabular;
    const { selection, setSelection, dragging, setDragging } = useSelectionContext();
    const { table } = useTableContext();
    const renderDiv = useRef(null);
    const cellRef = useRef(null);
    const { cellData: editingCellData, updateCellData, startEditing, commitCellData } = useEditingContext();
    const inputRef = useRef(null);
    const view = useCodeMirrorViewContext();

    const editing =
        editingCellData?.rowIndex === rowIndex &&
        editingCellData?.cellIndex >= columnIndex &&
        editingCellData?.cellIndex < columnIndex + (cellData.multiColumn?.columnSpan ?? 1);

    const onMouseDown = useCallback(
        (event) => {
            if (editing || event.button !== 0) {
                return;
            }
            setDragging(true);
            document.getSelection()?.removeAllRanges?.();
            setSelection((current) => {
                if (event.shiftKey && current) {
                    return new TableSelection(current.from, { cell: columnIndex, row: rowIndex }).explode(table);
                }
                return new TableSelection({ cell: columnIndex, row: rowIndex }).explode(table);
            });
        },
        [columnIndex, editing, rowIndex, setDragging, setSelection, table],
    );

    const onMouseUp = useCallback(() => {
        if (dragging) {
            setDragging(false);
        }
    }, [dragging, setDragging]);

    const onMouseMove = useCallback(
        (event) => {
            if (!dragging) {
                return;
            }
            if (event.buttons !== 1) {
                setDragging(false);
                return;
            }
            document.getSelection()?.removeAllRanges?.();
            if (selection?.to.cell === columnIndex && selection?.to.row === rowIndex) {
                return;
            }
            event.stopPropagation();
            setSelection((current) => {
                if (current) {
                    return new TableSelection(current.from, { row: rowIndex, cell: columnIndex }).explode(table);
                }
                return new TableSelection({ row: rowIndex, cell: columnIndex }).explode(table);
            });
        },
        [columnIndex, dragging, rowIndex, selection, setDragging, setSelection, table],
    );

    useEffect(() => {
        if (editing && inputRef.current) {
            inputRef.current.focus();
        }
    }, [editing, cellData.content.length]);

    const filterInput = useCallback((input) => {
        return input
            .replaceAll(/(^&|[^\\]&)/g, (match) => (match.length === 1 ? "\\&" : `${match[0]}\\&`))
            .replaceAll(/(^%|[^\\]%)/g, (match) => (match.length === 1 ? "\\%" : `${match[0]}\\%`))
            .replaceAll("\\\\", "");
    }, []);

    const isFocused =
        selection?.to.row === rowIndex &&
        selection?.to.cell >= columnIndex &&
        selection?.to.cell < columnIndex + (cellData.multiColumn?.columnSpan ?? 1);

    useEffect(() => {
        if (isFocused && !editing && cellRef.current) {
            cellRef.current.focus({ preventScroll: true });
        }
    }, [editing, isFocused]);

    useEffect(() => {
        const toDisplay = renderableCellContent(cellData.content.trim());
        if (renderDiv.current && !editing) {
            const tree = parser.parse(toDisplay);
            renderDiv.current.innerText = "";
            typesetNodeIntoElement(tree.topNode, renderDiv.current, toDisplay.substring.bind(toDisplay));
            loadMathJax()
                .then(async (MathJax) => {
                    const element = renderDiv.current;
                    if (element) {
                        await MathJax.typesetPromise([element]);
                        view.requestMeasure();
                        MathJax.typesetClear([element]);
                    }
                })
                .catch(() => {});
        }
    }, [cellData.content, editing, view]);

    const onInput = useCallback(
        (event) => {
            updateCellData(filterInput(event.target.value));
        },
        [filterInput, updateCellData],
    );

    const inSelection = selection?.contains({ row: rowIndex, cell: columnIndex }, table);

    const onDoubleClick = useCallback(() => {
        if (!view.state.readOnly) {
            startEditing(rowIndex, columnIndex, cellData.content);
        }
    }, [cellData.content, columnIndex, rowIndex, startEditing, view]);

    const body = editing ? (
        <CellInput
            className="table-generator-cell-input"
            value={editingCellData.content}
            onBlur={commitCellData}
            onInput={onInput}
            ref={inputRef}
        />
    ) : (
        <div ref={renderDiv} className="table-generator-cell-render" />
    );

    return (
        <td
            onDoubleClick={onDoubleClick}
            tabIndex={row.cells.length * rowIndex + columnIndex + 1}
            onMouseDown={onMouseDown}
            onMouseUp={onMouseUp}
            onMouseMove={onMouseMove}
            colSpan={cellData.multiColumn?.columnSpan}
            ref={cellRef}
            className={classNames(
                "table-generator-cell",
                columnSpecification.borderLeft > 0 && "table-generator-cell-border-left",
                columnSpecification.borderRight > 0 && "table-generator-cell-border-right",
                row.borderTop > 0 && "table-generator-row-border-top",
                row.borderBottom > 0 && "table-generator-row-border-bottom",
                columnSpecification.alignment === "left" && "alignment-left",
                columnSpecification.alignment === "center" && "alignment-center",
                columnSpecification.alignment === "right" && "alignment-right",
                columnSpecification.alignment === "paragraph" && "alignment-paragraph",
                inSelection && "selected",
                inSelection && selection?.bordersTop(rowIndex) && "selection-edge-top",
                inSelection && selection?.bordersBottom(rowIndex) && "selection-edge-bottom",
                inSelection && selection?.bordersLeft(rowIndex, columnIndex, table) && "selection-edge-left",
                inSelection && selection?.bordersRight(rowIndex, columnIndex, table) && "selection-edge-right",
                editing && "editing",
            )}
        >
            {body}
        </td>
    );
}
